// ─── useTheme – applies the resolved theme to <html> ──────────────────────────

import { useCallback, useEffect, useState } from 'react';
import {
  useThemeStore,
  resolveTheme,
  getSystemTheme,
  type ResolvedTheme,
} from '../store/themeStore';

/**
 * Reads the current theme preference, applies the resolved class
 * (`light` / `dark`) on `<html>`, and re-resolves whenever either the
 * preference or the OS-level setting changes.
 */
export function useTheme(): {
  preference: ReturnType<typeof useThemeStore.getState>['preference'];
  resolved: ResolvedTheme;
  setPreference: (p: 'light' | 'dark' | 'system') => void;
  toggle: () => void;
} {
  const preference = useThemeStore((s) => s.preference);
  const setPreference = useThemeStore((s) => s.setPreference);
  const toggle = useThemeStore((s) => s.toggle);

  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(preference));

  // Writes the resolved theme to <html>. Hoisted out of the effect below so the
  // OS-level listener can re-apply it too: updating `resolved` alone left the
  // stale `.dark` class and `style.colorScheme` on <html> when the OS flipped
  // while the user was on `system`, so the page and the native widgets it hosts
  // (select popups, scrollbars) disagreed about which scheme was in effect.
  const apply = useCallback((next: ResolvedTheme) => {
    const root = document.documentElement;
    root.classList.add('theme-transition');
    root.classList.toggle('dark', next === 'dark');
    root.style.colorScheme = next;
    setResolved(next);
    // Drop the transition helper after the swap finishes so static
    // hover/focus transitions aren't doubled-up.
    window.setTimeout(() => root.classList.remove('theme-transition'), 220);
  }, []);

  // Apply theme class to <html> + flip the native color-scheme.
  useEffect(() => {
    apply(resolveTheme(preference));
  }, [preference, apply]);

  // Track OS-level preference when user is in `system` mode.
  useEffect(() => {
    if (preference !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => apply(getSystemTheme());
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, [preference, apply]);

  return { preference, resolved, setPreference, toggle };
}
